import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    classSubjectApi,
    type ClassSubject,
    type ClassSubjectFilters,
    type CreateClassSubjectData,
    type UpdateClassSubjectData,
} from "@/api/classSubject";

export const useClassSubjectStore = defineStore("classSubject", () => {
    // State
    const classSubjects = ref<ClassSubject[]>([]);
    const currentClassSubject = ref<ClassSubject | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const classId = ref("");
    const subjectId = ref("");
    const teacherId = ref("");
    const semesterId = ref("");
    const isActive = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeClassSubjects = computed(() =>
        classSubjects.value.filter((classSubject) => classSubject.is_active),
    );

    const inactiveClassSubjects = computed(() =>
        classSubjects.value.filter((classSubject) => !classSubject.is_active),
    );

    const classSubjectsByClass = computed(() => {
        const grouped: Record<string, ClassSubject[]> = {};
        classSubjects.value.forEach((classSubject) => {
            const className =
                classSubject.class?.grade_level +
                    " " +
                    classSubject.class?.name || "Unknown Class";
            if (!grouped[className]) grouped[className] = [];
            grouped[className].push(classSubject);
        });
        return grouped;
    });

    const classSubjectsBySubject = computed(() => {
        const grouped: Record<string, ClassSubject[]> = {};
        classSubjects.value.forEach((classSubject) => {
            const subjectName = classSubject.subject?.name || "Unknown Subject";
            if (!grouped[subjectName]) grouped[subjectName] = [];
            grouped[subjectName].push(classSubject);
        });
        return grouped;
    });

    // Actions
    const fetchClassSubjects = async (filters: ClassSubjectFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await classSubjectApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                class_id: classId.value || undefined,
                subject_id: subjectId.value || undefined,
                teacher_id: teacherId.value || undefined,
                semester_id: semesterId.value || undefined,
                is_active:
                    isActive.value !== ""
                        ? isActive.value === "true"
                        : undefined,
                ...filters,
            });

            classSubjects.value = response.data?.items || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch class subjects";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassSubject = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentClassSubject.value = (
                await classSubjectApi.getById(id)
            ).data;
            return currentClassSubject.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch class subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createClassSubject = async (data: CreateClassSubjectData) => {
        loading.value = true;
        error.value = null;

        try {
            const newClassSubject = await classSubjectApi.create(data);
            // Refresh list to ensure consistency
            await fetchClassSubjects();
            return newClassSubject;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create class subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateClassSubject = async (
        id: string,
        data: UpdateClassSubjectData,
    ) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedClassSubject = await classSubjectApi.update(id, data);
            const index = classSubjects.value.findIndex((cs) => cs.id === id);
            if (index !== -1) {
                classSubjects.value[index] = updatedClassSubject.data;
            }
            if (currentClassSubject.value?.id === id) {
                currentClassSubject.value = updatedClassSubject.data;
            }
            return updatedClassSubject;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update class subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteClassSubject = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await classSubjectApi.delete(id);
            classSubjects.value = classSubjects.value.filter(
                (cs) => cs.id !== id,
            );
            if (currentClassSubject.value?.id === id) {
                currentClassSubject.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete class subject";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const toggleClassSubjectStatus = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedClassSubject = await classSubjectApi.toggleStatus(id);
            const index = classSubjects.value.findIndex((cs) => cs.id === id);
            if (index !== -1) {
                classSubjects.value[index] = updatedClassSubject.data;
            }
            if (currentClassSubject.value?.id === id) {
                currentClassSubject.value = updatedClassSubject.data;
            }
            return updatedClassSubject;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to toggle class subject status";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassSubjectsByClass = async (classIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classSubjectsByClass =
                await classSubjectApi.getByClass(classIdValue);
            return classSubjectsByClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch class subjects";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassSubjectsByTeacher = async (teacherIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classSubjectsByTeacher =
                await classSubjectApi.getByTeacher(teacherIdValue);
            return classSubjectsByTeacher;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch class subjects";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassSubjectsBySemester = async (semesterIdValue: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classSubjectsBySemester =
                await classSubjectApi.getBySemester(semesterIdValue);
            return classSubjectsBySemester;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch class subjects";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
    };

    const resetFilters = async () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        classId.value = "";
        subjectId.value = "";
        teacherId.value = "";
        semesterId.value = "";
        isActive.value = "";
        await fetchClassSubjects();
    };

    return {
        // State
        classSubjects,
        currentClassSubject,
        loading,
        error,
        page,
        perPage,
        search,
        classId,
        subjectId,
        teacherId,
        semesterId,
        isActive,
        meta,

        // Getters
        activeClassSubjects,
        inactiveClassSubjects,
        classSubjectsByClass,
        classSubjectsBySubject,

        // Actions
        fetchClassSubjects,
        fetchClassSubject,
        createClassSubject,
        updateClassSubject,
        deleteClassSubject,
        toggleClassSubjectStatus,
        fetchClassSubjectsByClass,
        fetchClassSubjectsByTeacher,
        fetchClassSubjectsBySemester,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
